import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, Search, ArrowLeft, Send, Check, CheckCheck, Users } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMessagesPage = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const intervalRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/conversations`);
      setConversations(res.data);
    } catch (error) { console.error('Failed to load conversations:', error); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);


  const openConvo = async (convo) => {
    setSelectedConvo(convo);
    try {
      const res = await axios.get(`${API}/messages/conversations/${convo.id}/messages`);
      setMessages(res.data);
    } catch (error) { console.error('Failed to load messages:', error); }
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/messages/conversations/${convo.id}/messages`);
        setMessages(res.data);
      } catch (error) { console.error('Failed to poll messages:', error); }
    }, 15000);
  };

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending || !selectedConvo) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/messages/conversations/${selectedConvo.id}/messages`, { text: text.trim() });
      setMessages(prev => [...prev, res.data]);
      setText('');
    } catch (error) { console.error('Failed to send message:', error); } finally { setSending(false); }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  const filteredConversations = conversations.filter(c =>
    c.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.distributor_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-messages-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Mensajes
        </h1>
        <p className="text-sm text-gray-500 mt-1">Todas las conversaciones entre distribuidores y clientes</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
        <div className="flex h-full">
          {/* Sidebar - Conversations */}
          <div className="w-80 border-r border-gray-200 flex flex-col flex-shrink-0">
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <Input placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-9 text-sm bg-gray-50 border-gray-100 rounded-lg"
                  data-testid="admin-search-conversations" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="w-8 h-8 rounded-full border-3 border-emerald-100 border-t-emerald-500 animate-spin" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="p-6 text-center">
                  <MessageCircle size={28} className="text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-400">Sin conversaciones</p>
                </div>
              ) : (
                filteredConversations.map((convo) => (
                  <button
                    key={convo.id}
                    onClick={() => openConvo(convo)}
                    className={`w-full p-3 text-left border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                      selectedConvo?.id === convo.id ? 'bg-[#F2F7F0]' : ''
                    }`}
                    data-testid={`admin-convo-${convo.id}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-xs">{convo.client_name?.charAt(0)?.toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-gray-900 text-xs truncate">{convo.client_name}</p>
                          <span className="text-[9px] text-gray-400 flex-shrink-0">{formatTime(convo.last_message_at)}</span>
                        </div>
                        <p className="text-[10px] text-gray-400 truncate">
                          <Users size={9} className="inline mr-0.5" /> {convo.distributor_name}
                        </p>
                        <p className="text-[10px] text-gray-500 truncate mt-0.5">{convo.last_message || 'Sin mensajes'}</p>
                      </div>
                      {convo.unread_count > 0 && (
                        <span className="w-4 h-4 bg-[#467322] text-white text-[8px] font-bold rounded-full flex items-center justify-center flex-shrink-0">
                          {convo.unread_count}
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col">
            {!selectedConvo ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle size={40} className="text-gray-200 mx-auto mb-3" />
                  <p className="text-sm text-gray-400">Selecciona una conversación</p>
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                      <span className="text-white font-bold text-xs">{selectedConvo.client_name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm">{selectedConvo.client_name}
                        {selectedConvo.client_display_id && (
                          <span className="ml-2 text-[10px] font-mono text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">{selectedConvo.client_display_id}</span>
                        )}
                      </p>
                      <p className="text-[10px] text-gray-500">Distribuidor: <span className="font-semibold">{selectedConvo.distributor_name}</span></p>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-1">
                  {messages.map(m => {
                    const isMine = m.sender_id === user?.id;
                    return (
                      <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-1`}>
                        <div className={`max-w-[65%] px-3.5 py-2 rounded-2xl ${
                          isMine ? 'bg-[#467322] text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md'
                        }`}>
                          {!isMine && <p className="text-[10px] font-bold text-[#467322] mb-0.5">{m.sender_name}</p>}
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</p>
                          <div className={`flex items-center justify-end gap-1 mt-0.5 ${isMine ? 'text-white/60' : 'text-gray-400'}`}>
                            <span className="text-[9px]">{formatTime(m.created_at)}</span>
                            {isMine && (m.read ? <CheckCheck size={12} /> : <Check size={12} />)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={handleSend} className="p-3 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <input type="text" value={text} onChange={(e) => setText(e.target.value)}
                      placeholder="Escribe como administrador..."
                      className="flex-1 bg-gray-50 border border-gray-100 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-gray-200"
                      data-testid="admin-chat-input" />
                    <button type="submit" disabled={!text.trim() || sending}
                      className="w-10 h-10 rounded-full bg-[#467322] text-white flex items-center justify-center shadow-lg disabled:opacity-40 hover:shadow-xl transition-all"
                      data-testid="admin-chat-send">
                      <Send size={16} />
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMessagesPage;
